import React from 'react'
import { Teacher } from '../../providers/context'
import { ThreePanelLayout } from '../../layouts/threePanel'
import { ListButton } from '../../components/simple/listButton'
import {
  IconAssessment,
  IconAttendance,
  IconCommentSlash,
  IconConference,
  IconGraduationCap,
  IconHome,
  IconSent,
  IconStream,
  IconSurvey,
  IconTable,
  IconTeacher,
} from '../../icons/library'
import { useRequest } from '../../request'
import { InboxTab, UnreadCounts } from '../home/inboxes'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { IconHelp } from '../../icons/help'
import { Divider } from '../../components/simple/divider'
import {
  CommunicationsWidget,
  ContactMethodsWidget,
  EngagementWidget,
  ExportWidget,
  LanguagesWidget,
  MessagesWidget,
  TopUsersWidget,
  UsageChartWidget,
  UsersWidget,
} from './widgets'
import { getComposeLink, getDirectLink, URLAction } from '../../providers/URLParams'
import { IconAnnouncementCall } from '../../icons/announcementCall'
import { ButtonStyle } from '../../components/simple/button'
import { Redirect } from '../../routerComponents'

export const DashboardPage: React.FC<{
  user: Teacher
  schoolID: number | undefined
}> = ({ user, schoolID }) => {
  const schools = user.districtAccess ? user.district.schools : user.schools
  if (schoolID !== undefined && !schools.find((s) => s.id === schoolID)) return <Redirect to={`/dashboard`} />

  const { data: unreadCounts } = useRequest<UnreadCounts>(
    '/frontend_api/unreadCounts',
    React.useMemo(
      () => ({
        thisYearsMessages: true,
      }),
      []
    )
  )

  return (
    <ThreePanelLayout
      noMobile
      user={user}
      left={
        <>
          <ListButton inset buttonStyle={ButtonStyle.logo} link="/">
            <IconHome className="mr-2" />
            See Your Messages
            {unreadCounts && unreadCounts.inbox > 0 && (
              <Badge className="ml-auto" badgeStyle={BadgeStyle.white}>
                {unreadCounts.inbox}
              </Badge>
            )}
          </ListButton>
          <ListButton inset buttonStyle={ButtonStyle.warning} className="text-neutral-900" link={getComposeLink({})}>
            <IconSent className="mr-2" />
            New Message
          </ListButton>
          {user.district.massCallingEnabled && (
            <ListButton
              inset
              buttonStyle={ButtonStyle.warning}
              className="text-neutral-900"
              link={getComposeLink({ type: 'mass' })}
            >
              <IconAnnouncementCall className="mr-2" /> New Broadcast
            </ListButton>
          )}
          {user.showAttendance && (
            <ListButton
              inset
              buttonStyle={ButtonStyle.warning}
              className="text-neutral-900"
              link={getDirectLink({
                [URLAction.OpenInboxTab]: InboxTab.Attendance,
              })}
            >
              <IconAttendance className="mr-2" /> View Attendance
            </ListButton>
          )}
          {user.showPTCConferences && (
            <ListButton
              inset
              buttonStyle={ButtonStyle.warning}
              className="text-neutral-900"
              link={getDirectLink({
                [URLAction.OpenInboxTab]: InboxTab.Conference,
              })}
            >
              <IconConference className="-ml-0.5 mr-1" /> View Conferences
            </ListButton>
          )}
          {user.showSurveys && (
            <ListButton inset highlighted link={getDirectLink({ [URLAction.OpenInboxTab]: InboxTab.Survey })}>
              <IconSurvey className="mr-2" /> Forms
            </ListButton>
          )}
          {user.showAssessments && (
            <ListButton inset highlighted link={getDirectLink({ [URLAction.OpenInboxTab]: InboxTab.Assessment })}>
              <IconAssessment className="-ml-1 mr-1" /> Assessments
            </ListButton>
          )}
          <Divider className="mt-2 !border-charcoal-active" />
          <div className="flex min-h-[120px] flex-col gap-0.5 overflow-y-scroll">
            <div className="mt-2 font-bold">Schools</div>
            {user.districtAccess && (
              <ListButton className="!ml-2" thin lightHighlight={schoolID === undefined} link="/dashboard/district">
                District
              </ListButton>
            )}
            {schools.map((s) => (
              <ListButton
                className="!ml-2"
                thin
                key={s.id}
                lightHighlight={schoolID === s.id}
                link={`/dashboard/school/${s.id}`}
              >
                {s.name}
              </ListButton>
            ))}
          </div>
          <Divider className="!border-charcoal-active" />

          <div className="mt-2 font-bold">Quick Links</div>
          <ListButton lightHighlight link="/contacts/families">
            <IconGraduationCap />
            Student Contacts
          </ListButton>
          <ListButton lightHighlight link="/contacts/teachers">
            <IconTeacher />
            Teacher Contacts
          </ListButton>
          <ListButton lightHighlight link="/contacts/report">
            <IconCommentSlash />
            Contact Report
          </ListButton>
          <ListButton lightHighlight link="/stats/district">
            <IconTable />
            {user.districtAccess ? 'District' : 'School'} Stats
          </ListButton>
          {user.districtAccess && user.admin === 2 && (
            <ListButton lightHighlight link="/integration">
              <IconStream />
              Integration Status
            </ListButton>
          )}
          <div className="mt-auto font-bold">Need Help?</div>
          <ListButton lightHighlight link="https://reachmyteach.zendesk.com">
            <IconHelp />
            Help Center
          </ListButton>
        </>
      }
    >
      <div className="flex min-h-full flex-col gap-6 overflow-auto bg-lightBlue p-6">
        <div className="flex gap-6">
          <div className="flex flex-col gap-6">
            <UsersWidget schoolID={schoolID} />
            <EngagementWidget schoolID={schoolID} />
            <ContactMethodsWidget />
          </div>
          <div className="flex flex-col gap-6">
            <CommunicationsWidget schoolID={schoolID} />
            <MessagesWidget schoolID={schoolID} />
            <LanguagesWidget schoolID={schoolID} />
          </div>
          <div className="flex flex-col gap-6">
            <TopUsersWidget schoolID={schoolID} />
            {user.admin === 2 && <ExportWidget />}
          </div>
        </div>
        <UsageChartWidget schoolID={schoolID} />
      </div>
    </ThreePanelLayout>
  )
}
